import { computed } from 'vue'
import { useLocaleStore, type AppLocale } from '../stores/locale'
import { en } from './en'
import { es } from './es'
import { pt } from './pt'

type MessageTree = { [key: string]: string | MessageTree }

const DICTS: Record<AppLocale, MessageTree> = { en, es, pt }

function lookup(tree: MessageTree, key: string): string | undefined {
  let cur: string | MessageTree | undefined = tree
  for (const part of key.split('.')) {
    if (!cur || typeof cur === 'string') return undefined
    cur = cur[part]
  }
  return typeof cur === 'string' ? cur : undefined
}

function interpolate(
  raw: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return raw
  return raw.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = vars[name]
    return value === undefined ? `{${name}}` : String(value)
  })
}

/**
 * Resolve a dotted key against the current locale (falls back to English).
 * Safe to call from Vue templates and from Phaser scenes.
 */
export function t(
  key: string,
  vars?: Record<string, string | number>,
): string {
  const locale = useLocaleStore().locale
  const raw = lookup(DICTS[locale], key) ?? lookup(DICTS.en, key) ?? key
  return interpolate(raw, vars)
}

/** Reactive `t()` for Vue. Re-renders when the locale store changes. */
export function useI18n() {
  const store = useLocaleStore()
  const locale = computed(() => store.locale)
  const translate = (
    key: string,
    vars?: Record<string, string | number>,
  ): string => {
    const current = locale.value
    return interpolate(
      lookup(DICTS[current], key) ?? lookup(DICTS.en, key) ?? key,
      vars,
    )
  }
  return { t: translate, locale, setLocale: store.setLocale }
}
